import React, { useEffect, useState } from "react";

// Shared lightweight loading indicator + top-right load-time badge, reused across sections.
// - LoadingOverlay: a light, non-blocking indicator — a slim indeterminate top progress bar plus
//   a small centred pill (spinner + label + a subtle rotating stage word). No live timer, no
//   full-screen blur/dim, so it stays visible without pulling attention or covering the screen.
// - LoadMsBadge: top-right pill showing the measured load→screen time in ms.
// - useLoadTimer(loading): returns the ms of the last load (measured to actual paint via double rAF).

const CSS = `
.lp-ov{position:fixed;top:0;left:0;right:0;z-index:60;pointer-events:none;animation:lpfade .2s ease}
@keyframes lpfade{from{opacity:0}to{opacity:1}}
.lp-topbar{position:relative;height:3px;width:100%;background:rgba(88,86,214,.12);overflow:hidden}
.lp-topfill{position:absolute;top:0;height:100%;width:34%;border-radius:999px;background:linear-gradient(90deg,rgba(88,86,214,0),#5856D6,#8B8AF0,#5856D6,rgba(88,86,214,0));animation:lpslide 1.15s ease-in-out infinite}
@keyframes lpslide{0%{left:-40%}100%{left:100%}}
.lp-pill{position:absolute;top:12px;left:50%;transform:translateX(-50%);display:inline-flex;align-items:center;gap:9px;background:rgba(255,255,255,.94);border:1px solid #E9E9EC;box-shadow:0 8px 22px -14px rgba(20,20,18,.28);border-radius:999px;padding:7px 15px;-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px)}
.lp-spin{width:14px;height:14px;border-radius:50%;border:2px solid #E4E4F5;border-top-color:#5856D6;animation:lpspin .7s linear infinite;flex:none}
@keyframes lpspin{to{transform:rotate(360deg)}}
.lp-title{font-size:12.5px;font-weight:700;color:#141412}
.lp-stage{font-size:12px;font-weight:600;color:#8A8A88;transition:opacity .25s ease}
.lp-stage::before{content:"·";margin-right:8px;color:#C9C9CE}
.lp-badge{position:absolute;top:14px;right:20px;z-index:20;display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;font-variant-numeric:tabular-nums;color:#4A4AC2;background:#ecebfb;border:1px solid #d7d5f5;border-radius:999px;padding:4px 11px}
.lp-badge .dot{width:6px;height:6px;border-radius:50%;background:#5856D6}
`;

function useInjectCss() {
  useEffect(() => {
    if (document.getElementById("lp-perf-css")) return;
    const el = document.createElement("style");
    el.id = "lp-perf-css";
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

const DEFAULT_STAGES = ["Loading", "Classifying", "Patient", "Status", "Optimizing"];

export function LoadingOverlay({ label = "Loading", stages = DEFAULT_STAGES }) {
  useInjectCss();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const si = setInterval(() => setStage((s) => (s + 1) % stages.length), 700);
    return () => clearInterval(si);
  }, [stages.length]);
  return (
    <div className="lp-ov" data-testid="lp-loading-overlay">
      <div className="lp-topbar"><div className="lp-topfill" /></div>
      <div className="lp-pill">
        <span className="lp-spin" />
        <span className="lp-title">{label}</span>
        {stages.length ? <span className="lp-stage" data-testid="lp-loading-stage">{stages[stage]}</span> : null}
      </div>
    </div>
  );
}

export function LoadMsBadge({ ms }) {
  useInjectCss();
  if (ms == null) return null;
  return (
    <div className="lp-badge" data-testid="lp-load-ms" title="Time to load this page (data + render to screen)">
      <span className="dot" /> {Math.round(ms)} ms
    </div>
  );
}

// Measures load duration to actual paint. Starts timing when `loading` turns true,
// and on the following false transition reports ms after a double requestAnimationFrame.
export function useLoadTimer(loading) {
  const [ms, setMs] = useState(null);
  const t0 = React.useRef(null);
  useEffect(() => {
    if (loading) { t0.current = performance.now(); return; }
    if (t0.current == null) return;
    const start = t0.current; t0.current = null;
    requestAnimationFrame(() => requestAnimationFrame(() => setMs(performance.now() - start)));
  }, [loading]);
  return ms;
}
